import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Trans } from '@lingui/react/macro';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { searchResidentialUnitDocuments, ResidentialUnitDocumentSearchOptions } from '@/services/documents';
import DocumentList from '@/seller/components/documents/DocumentList/DocumentList';
import { uuidv4 } from '@/utils/uuid';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { ProjectPermissionToggle } from '@/components/permissions';
import { faUpload, faFileArchive } from '@fortawesome/pro-light-svg-icons';
import { ResidentialUnitDocument, ChangedItem, BaseDocument } from '@/types';
import { IState } from '@/state/IState';
import { useAsyncState, useDefaultScrollToStringId, useMergeState, useEffectSkipFirst } from '@/utils/hooks';
import { DocumentTypeGrouping, DocumentTypeGroupingHeader } from '@/components/DocumentTypeGrouping';
import DocumentUploadWizardDialog from '@/seller/components/documents/DocumentUploadWizardDialog';
import DocumentUploadDialog from '@/seller/components/documents/DocumentUploadDialog';
import DocumentZipDownloadDialog from '@/seller/components/documents/DocumentZipCreateDialog';
import { Button, ButtonBar, SlideToggle, Tabs } from '@ygtechnologies/rubus-ui';

type DocumentDisplayMode = 'All' | 'ResidentialUnit' | 'Project';

const mapStateToProps = (state: IState) => ({
    project: state.global.activeProject!,
    currentCulture: state.global.currentCulture,
});

type OwnProps = ReturnType<typeof mapStateToProps> & {
    residentialUnitId: number;
};

const ResidentialUnitDocuments: React.FC<OwnProps> = ({ residentialUnitId, currentCulture, project }) => {
    const [filters, mergeFilters] = useMergeState<ResidentialUnitDocumentSearchOptions>({
        residentialUnitIds: [residentialUnitId],
        includeArchived: false,
        projectId: project.id,
        includeUnpublishedVersionsCount: true,
    });
    const [documentZipDialogIsOpen, setDocumentZipDialogIsOpen] = useState(false);

    useEffectSkipFirst(() => {
        setDocuments(null);
        mergeFilters({ residentialUnitIds: [residentialUnitId] });
    }, [residentialUnitId, mergeFilters]);

    const [documents, setDocuments, reloadDocuments] = useAsyncState(
        () => searchResidentialUnitDocuments(filters),
        [filters, currentCulture],
        null
    );
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [showUploadWizardDialog, setShowUploadWizardDialog] = useState(false);
    const [uploadedDocument, setUploadedDocument] = useState<null | any>(null);
    // const [documentToReplace, setDocumentToReplace] = useState(false);

    const [displayMode, setDisplayMode] = useState<DocumentDisplayMode>('All');
    const [filteredDocuments, setFilteredDocuments] = useState<ResidentialUnitDocument[] | null>(null);

    const defaultExpandedId = useDefaultScrollToStringId();

    useEffect(() => {
        let data = documents;

        if (!data) {
            setFilteredDocuments(null);
            return;
        }

        if (displayMode === 'Project') {
            data = data.filter((x) => x.isProjectDocument);
        } else if (displayMode === 'ResidentialUnit') {
            data = data.filter((x) => !x.isProjectDocument);
        }

        setFilteredDocuments(data);
    }, [documents, displayMode]);

    const handleDocumentsUploaded = () => {
        setUploadedDocument(null);
        reloadDocuments();
    };

    const handleReplacementFileDrop = (file: File | null, document: BaseDocument | null) => {
        const fileData = file ? new FileData(file) : null;
        const mappedFile = {
            documentId: document && document.documentId ? document.documentId : uuidv4(),
            fileData: fileData,
            name: fileData ? fileData.getFileNameWithoutExtension() : '',
            floor: document ? document.floor : 1,
            documentTypeId: document ? document.documentTypeId : null,
            replacement: !!document,
            isSensitive: document?.isSensitive ?? false,
            hideFromBuyer: (document as ResidentialUnitDocument)?.hideFromBuyer ?? false,
        };
        setUploadedDocument(mappedFile);
        setShowUploadDialog(true);
    };

    const handleChange = (changes: ChangedItem<BaseDocument>[]) => {
        if (!documents) {
            return;
        }

        reloadDocuments();
    };
    const newDocumentUploadDialogProps = {
        projectId: project.id,
        document: uploadedDocument || null,
        residentialUnitId: residentialUnitId,
        onSave: handleDocumentsUploaded,
    };
    return (
        <React.Fragment>
            <ButtonBar sticky margins="none">
                <Tabs
                    activeId={displayMode}
                    onChange={(id) => setDisplayMode(id as DocumentDisplayMode)}
                    variant="pill"
                    size="sm">
                    <Tabs.Tab id="All" label={<Trans>All</Trans>} />
                    <Tabs.Tab id="Project" label={<Trans>Project documents</Trans>} />
                    <Tabs.Tab id="ResidentialUnit" label={<Trans>Unit documents</Trans>} />
                </Tabs>

                <ButtonBar.Right>
                    <SlideToggle
                        onChange={(e) => mergeFilters({ includeArchived: e.target.checked })}
                        checked={filters.includeArchived}>
                        <Trans>Show archived documents</Trans>
                    </SlideToggle>

                    <Button onClick={() => setDocumentZipDialogIsOpen(true)} icon={faFileArchive} variant="tertiary">
                        <Trans>Download to zip file</Trans>
                    </Button>

                    <ProjectPermissionToggle condition="canUploadDocuments">
                        {displayMode !== 'Project' && (
                            <Button variant="tertiary" onClick={(e) => setShowUploadWizardDialog(true)} icon={faUpload}>
                                <Trans>Upload unit document</Trans>
                            </Button>
                        )}

                        <DocumentUploadDialog
                            {...newDocumentUploadDialogProps}
                            onClose={() => setShowUploadDialog(!showUploadDialog)}
                            isOpen={showUploadDialog}
                        />

                        <DocumentUploadWizardDialog
                            {...newDocumentUploadDialogProps}
                            onClose={() => setShowUploadWizardDialog(!showUploadWizardDialog)}
                            isOpen={showUploadWizardDialog}
                        />

                        {/*Replacement dialog */}
                        {/* <DocumentUploadDialog
                        {...newDocumentUploadDialogProps}
                        toggle={() => setShowReplacementDialog(!showReplacementDialog)}
                        // defaultUploadType={'document'}
                        isOpen={showReplacementDialog}
                    /> */}
                    </ProjectPermissionToggle>
                </ButtonBar.Right>
            </ButtonBar>

            {filteredDocuments ? (
                <DocumentTypeGrouping items={filteredDocuments}>
                    {(items, groupInfo, key) => (
                        <React.Fragment key={key}>
                            <DocumentTypeGroupingHeader value={groupInfo} />
                            <DocumentList
                                defaultExpandedId={defaultExpandedId}
                                mode="ResidentialUnit"
                                documents={items}
                                onFileDrop={handleReplacementFileDrop}
                                onChange={handleChange}
                            />
                        </React.Fragment>
                    )}
                </DocumentTypeGrouping>
            ) : (
                <LoadingIndicator />
            )}
            <DocumentZipDownloadDialog
                filters={filters}
                toggle={() => setDocumentZipDialogIsOpen(false)}
                isOpen={documentZipDialogIsOpen}
            />
        </React.Fragment>
    );
};

export default connect(mapStateToProps)(ResidentialUnitDocuments);
